import { QueryClient, useQuery } from '@tanstack/react-query';
import request from '@utils/request';
import { fetchSpaEntityParams } from './types';
import { sortEntityByPriority } from '@utils/helpers';
import { PaginatedSpaEntityType, SpaEntityType } from 'shared-types/Spa';
import { SPA_AVAILABILITY_TIMEOUT } from './consts';
import { Routes } from '@common/constants';

export const getSpaTreatments = async (
  propertyId: number,
  language: string,
  params: fetchSpaEntityParams
) => {
  const { data } = await request.get(`${propertyId}/spa-treatments/${language}`, {
    params,
    timeout: SPA_AVAILABILITY_TIMEOUT,
  });
  const treatments = data.data.map((treatment) => ({
    ...treatment,
    linkRoute: Routes.SPA_TREATMENT_DETAIL,
    linkParams: { id: treatment.id },
  }));
  return { data: treatments, total: data.total } as PaginatedSpaEntityType;
};

export const getAvailableSpaTreatmentsPrefetch = async (
  queryClient: QueryClient,
  propertyId: number,
  language: string,
  arrivalDate: string | null,
  category: number | null,
  perPage = 0,
  sortByPriority = true,
  guests?: number
) => {
  await queryClient.prefetchQuery(
    getAvailableSpaTreatmentsKey(propertyId, language, {
      arrivalDate,
      category,
      perPage,
      sortByPriority,
      guests,
    }),
    () =>
      getSpaTreatments(propertyId, language, {
        arrivalDate,
        category,
        perPage,
        numberOfGuests: guests,
      }),
    { cacheTime: Infinity, staleTime: Infinity }
  );
};

export const useGetSpaTreatments = (
  propertyId: number,
  language: string,
  arrivalDate: string | null,
  category: number | null,
  perPage = 0,
  sortByPriority = true,
  guests?: number,
  enabled = true
) => {
  const { data, isFetching, isError, error, refetch, errorUpdatedAt, dataUpdatedAt } = useQuery({
    queryKey: getAvailableSpaTreatmentsKey(propertyId, language, {
      arrivalDate,
      category,
      perPage,
      sortByPriority,
      guests,
    }),
    queryFn: () =>
      getSpaTreatments(propertyId, language, {
        arrivalDate,
        category,
        perPage,
        numberOfGuests: guests,
      }),
    cacheTime: Infinity,
    staleTime: Infinity,
    keepPreviousData: true,
    enabled,
  });

  if (!data?.data) {
    return {
      spaTreatments: [],
      isFetching,
      isError,
      error,
      errorUpdatedAt,
      dataUpdatedAt,
      refetch,
      total: 0,
    };
  }

  const spaTreatments = sortByPriority ? sortEntityByPriority(data?.data) : data?.data;

  return {
    spaTreatments: spaTreatments as SpaEntityType[],
    isFetching,
    isError,
    error,
    errorUpdatedAt,
    dataUpdatedAt,
    refetch,
    total: data?.total,
  };
};

export const getAvailableSpaTreatmentsKey = (
  propertyId: number,
  language: string,
  params: {
    category: number | null;
    perPage?: number;
    arrivalDate: string | null;
    sortByPriority: boolean;
    guests?: number;
  }
) => ['spaTreatments', 'available', propertyId, ...Object.values(params), language];
